(function () {
  'use strict';

  var COMPONENT_NAME = 'lifta-mix-match-bundle';
  var SELECTORS = {
    data: '[data-mix-match-products]',
    tier: '[data-mix-match-tier]',
    row: '[data-mix-match-row]',
    product: '[data-mix-match-product]',
    variant: '[data-mix-match-variant]',
    image: '[data-mix-match-image]',
    imageWrap: '[data-mix-match-image-wrap]',
    imagePlaceholder: '[data-mix-match-image-placeholder]',
    selectionHeadingSingle: '[data-mix-match-selection-heading-single]',
    selectionHeadingMultiple: '[data-mix-match-selection-heading-multiple]',
    help: '[data-mix-match-help]',
    add: '[data-mix-match-add], [data-mix-match-submit]',
    buttonText: '[data-mix-match-button-text], [data-mix-match-add-text]',
    error: '[data-mix-match-error]',
    tierPrice: '[data-mix-match-tier-price]',
    tierNormal: '[data-mix-match-tier-normal]',
    tierDiscounted: '[data-mix-match-tier-discounted]',
    tierSavings: '[data-mix-match-tier-savings]',
    tierEstimate: '[data-mix-match-tier-estimate]'
  };

  var MESSAGES = {
    chooseColor: 'Valitse väri paidalle [row].',
    chooseSize: 'Valitse koko paidalle [row].',
    unavailable: 'Valittu koko paidalle [row] ei ole saatavilla. Valitse toinen koko.',
    invalidMarkup: 'Valintoja ei voitu näyttää oikein. Päivitä sivu ja yritä uudelleen.',
    invalidData: 'Mix & match -tuotetietoja ei voitu ladata. Päivitä sivu ja yritä uudelleen.',
    addFailed: 'Kaikkia valittuja tuotteita ei voitu lisätä. Tarkista valinnat ja ostoskori.',
    added: 'Lisätty ostoskoriin',
    chooseColorOption: 'Valitse väri',
    chooseSizeOption: 'Valitse koko',
    soldOutSuffix: ' – loppuunmyyty',
    savingsPrefix: 'Säästä '
  };

  function idString(value) {
    return value === null || typeof value === 'undefined' ? '' : String(value);
  }

  function integer(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? Math.round(parsed) : fallback;
  }

  function localeRoute(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    return String(root).replace(/\/?$/, '/') + String(path).replace(/^\//, '');
  }

  class LiftaMixMatchBundle extends HTMLElement {
    connectedCallback() {
      if (!this._initialized) {
        this._initialized = true;

        try {
          this._initialize();
        } catch (error) {
          this._showFatalError();
          if (window.Shopify && window.Shopify.designMode) {
            console.error('Lifta mix & match:', error);
          }
        }
      }

      this._attachListeners();
    }

    disconnectedCallback() {
      this._detachListeners();

      if (this._successTimer) {
        window.clearTimeout(this._successTimer);
        this._successTimer = null;
      }
    }

    _initialize() {
      var dataElement = this.querySelector(SELECTORS.data);
      if (!dataElement) {
        throw new Error('Missing product JSON');
      }

      // Read and parse the Liquid-generated payload exactly once per element.
      var parsed = JSON.parse(dataElement.textContent);
      this.config = this._normalizeConfig(parsed);

      if (!this.config.products.length) {
        throw new Error('No campaign products');
      }

      this.rows = Array.from(this.querySelectorAll(SELECTORS.row));
      this.tierInputs = Array.from(this.querySelectorAll(SELECTORS.tier));
      this.singleSelectionHeading = this.querySelector(SELECTORS.selectionHeadingSingle);
      this.multipleSelectionHeading = this.querySelector(SELECTORS.selectionHeadingMultiple);
      this.helpElement = this.querySelector(SELECTORS.help);
      this.addButton = this.querySelector(SELECTORS.add);
      this.errorElement = this.querySelector(SELECTORS.error);
      this.buttonTextElement = this.addButton
        ? this.addButton.querySelector(SELECTORS.buttonText) || this.addButton
        : null;

      if (!this.addButton || !this.errorElement || this.tierInputs.length < this.config.tiers.length) {
        throw new Error('Incomplete component markup');
      }

      this.defaultButtonText = this.addButton.getAttribute('data-default-text') ||
        (this.buttonTextElement ? this.buttonTextElement.textContent.trim() : 'Lisää valinnat ostoskoriin');

      var defaultProduct = this._defaultProduct();
      var defaultTier = this._findTier(this.config.defaultTier) || this.config.tiers[0];
      this.state = {
        activeTier: defaultTier.key,
        activeQuantity: defaultTier.quantity,
        loading: false,
        validationAttempted: false,
        rows: []
      };

      for (var index = 0; index < this.config.maxQuantity; index += 1) {
        this.state.rows.push({
          productId: defaultProduct ? defaultProduct._id : '',
          variantId: '',
          sizeLabel: ''
        });
      }

      this._markupInvalid = this.rows.length < this.config.maxQuantity;
      this._onChange = this._handleChange.bind(this);
      this._onClick = this._handleClick.bind(this);
      this._render();

      if (this._markupInvalid) {
        this._setError(MESSAGES.invalidMarkup);
      }
    }

    _normalizeConfig(data) {
      var rawProducts = data && Array.isArray(data.products) ? data.products : [];
      var products = rawProducts.map(function (product) {
        var rawVariants = Array.isArray(product.variants) ? product.variants : [];
        var variants = rawVariants.map(function (variant) {
          return {
            id: variant.id,
            _id: idString(variant.id),
            title: variant.title || '',
            sizeLabel: variant.sizeLabel || variant.title || '',
            available: variant.available === true,
            price: Math.max(0, integer(variant.price, 0)),
            compareAtPrice: Math.max(0, integer(variant.compareAtPrice, 0))
          };
        });
        var variantPrices = variants.map(function (variant) { return variant.price; });
        var priceMin = integer(product.priceMin, variantPrices.length ? Math.min.apply(null, variantPrices) : 0);

        return {
          id: product.id,
          _id: idString(product.id),
          handle: product.handle || '',
          title: product.title || '',
          colorLabel: product.colorLabel || product.title || '',
          available: product.available === true,
          featuredImage: product.featuredImage || '',
          featuredImageAlt: product.featuredImageAlt || product.title || '',
          priceMin: Math.max(0, priceMin),
          variants: variants
        };
      });
      var fallbackTiers = [
        { key: 'single', quantity: 1, discountAmount: 0 },
        { key: 'three', quantity: 3, discountAmount: 895 },
        { key: 'five', quantity: 5, discountAmount: 2487 }
      ];
      var rawTiers = data && Array.isArray(data.tiers) ? data.tiers : fallbackTiers;
      var tierKeys = new Set();
      var tiers = rawTiers.reduce(function (normalized, tier) {
        var key = tier && typeof tier.key === 'string' ? tier.key.trim() : '';
        if (!/^[a-z0-9_-]+$/.test(key) || tierKeys.has(key)) {
          return normalized;
        }

        tierKeys.add(key);
        normalized.push({
          key: key,
          quantity: Math.max(1, Math.min(10, integer(tier.quantity, 1))),
          discountAmount: Math.max(0, integer(tier.discountAmount, 0))
        });
        return normalized;
      }, []);

      if (!tiers.length) {
        tiers = fallbackTiers;
      }

      var maxQuantity = tiers.reduce(function (maximum, tier) {
        return Math.max(maximum, tier.quantity);
      }, 1);
      var requestedDefaultTier = data && typeof data.defaultTier === 'string'
        ? data.defaultTier
        : 'three';
      var defaultTier = tiers.some(function (tier) { return tier.key === requestedDefaultTier; })
        ? requestedDefaultTier
        : tiers[0].key;

      return {
        currency: data && data.currency ? String(data.currency) : 'EUR',
        currentProductId: idString(data && data.currentProductId),
        tiers: tiers,
        maxQuantity: maxQuantity,
        defaultTier: defaultTier,
        products: products
      };
    }

    _defaultProduct() {
      var currentProduct = this._findProduct(this.config.currentProductId);
      if (currentProduct && this._hasAvailableVariant(currentProduct)) {
        return currentProduct;
      }

      return this.config.products.find(this._hasAvailableVariant.bind(this)) || this.config.products[0] || null;
    }

    _hasAvailableVariant(product) {
      return Boolean(product && product.variants.some(function (variant) { return variant.available; }));
    }

    _attachListeners() {
      if (this._listenersAttached || !this._onChange || !this._onClick) {
        return;
      }

      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      this._listenersAttached = true;
    }

    _detachListeners() {
      if (!this._listenersAttached) {
        return;
      }

      this.removeEventListener('change', this._onChange);
      this.removeEventListener('click', this._onClick);
      this._listenersAttached = false;
    }

    _handleClick(event) {
      var button = event.target.closest(SELECTORS.add);
      if (!button || !this.contains(button)) {
        return;
      }

      event.preventDefault();
      this._addSelectedItems();
    }

    _handleChange(event) {
      if (this.state.loading) {
        this._render();
        return;
      }

      if (event.target.matches(SELECTORS.tier)) {
        var tier = this._findTier(event.target.value);
        if (!tier) {
          return;
        }

        this.state.activeTier = tier.key;
        this.state.activeQuantity = tier.quantity;
        this.state.validationAttempted = false;
        this._clearError();
        this._render();
        return;
      }

      var rowElement = event.target.closest(SELECTORS.row);
      if (!rowElement || !this.contains(rowElement)) {
        return;
      }

      var rowIndex = this.rows.indexOf(rowElement);
      if (rowIndex < 0 || rowIndex >= this.state.rows.length) {
        return;
      }

      if (event.target.matches(SELECTORS.product)) {
        this._changeProduct(rowIndex, event.target.value);
      } else if (event.target.matches(SELECTORS.variant)) {
        this._changeVariant(rowIndex, event.target.value);
      }
    }

    _changeProduct(rowIndex, productId) {
      var rowState = this.state.rows[rowIndex];
      var product = this._findProduct(productId);
      var priorSize = rowState.sizeLabel;
      var matchingVariant = product && priorSize
        ? product.variants.find(function (variant) {
          return variant.available && variant.sizeLabel === priorSize;
        })
        : null;

      rowState.productId = product ? product._id : '';
      rowState.variantId = matchingVariant ? matchingVariant._id : '';
      rowState.sizeLabel = matchingVariant ? matchingVariant.sizeLabel : '';
      this._render();
    }

    _changeVariant(rowIndex, variantId) {
      var rowState = this.state.rows[rowIndex];
      var product = this._findProduct(rowState.productId);
      var variant = this._findVariant(product, variantId);

      if (variant && variant.available) {
        rowState.variantId = variant._id;
        rowState.sizeLabel = variant.sizeLabel;
      } else {
        rowState.variantId = '';
        rowState.sizeLabel = '';
      }

      this._render();
    }

    _render() {
      if (!this.state || !this.config) {
        return;
      }

      this.setAttribute('data-active-tier', this.state.activeTier);
      this.setAttribute('data-active-quantity', String(this.state.activeQuantity));

      var singleIsActive = this.state.activeQuantity === 1;
      if (this.singleSelectionHeading) {
        this.singleSelectionHeading.hidden = !singleIsActive;
      }
      if (this.multipleSelectionHeading) {
        this.multipleSelectionHeading.hidden = singleIsActive;
      }
      if (this.helpElement) {
        this.helpElement.hidden = singleIsActive;
      }

      this.tierInputs.forEach(function (input) {
        input.checked = input.value === this.state.activeTier;
        input.disabled = this.state.loading;
      }, this);

      this.rows.forEach(function (rowElement, index) {
        this._renderRow(rowElement, index);
      }, this);

      this._renderPrices();

      if (this.state.validationAttempted) {
        this._validate(false);
      } else {
        this._clearInvalidFields();
      }

      this._updateButton();
    }

    _renderRow(rowElement, index) {
      var isActive = index < this.state.activeQuantity && index < this.config.maxQuantity;
      var rowState = this.state.rows[index];

      rowElement.hidden = !isActive;
      rowElement.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      rowElement.classList.toggle('is-disabled', !isActive || this.state.loading);

      var productSelect = rowElement.querySelector(SELECTORS.product);
      var variantSelect = rowElement.querySelector(SELECTORS.variant);

      if (!rowState || !productSelect || !variantSelect) {
        this._markupInvalid = true;
        if (productSelect) productSelect.disabled = true;
        if (variantSelect) variantSelect.disabled = true;
        return;
      }

      this._renderProductOptions(productSelect, rowState.productId);
      var product = this._findProduct(rowState.productId);
      this._renderVariantOptions(variantSelect, product, rowState);
      this._renderImage(rowElement, product);

      var imageWrap = rowElement.querySelector(SELECTORS.imageWrap);
      rowElement.classList.toggle(
        'lifta-mix-match__row--without-image',
        Boolean(imageWrap && imageWrap.hidden)
      );

      productSelect.disabled = !isActive || this.state.loading;
      variantSelect.disabled = !isActive || this.state.loading || !product;
    }

    _renderProductOptions(select, selectedProductId) {
      var fragment = document.createDocumentFragment();
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = MESSAGES.chooseColorOption;
      fragment.appendChild(placeholder);

      this.config.products.forEach(function (product) {
        var option = document.createElement('option');
        option.value = product._id;
        option.textContent = product.colorLabel;
        option.selected = product._id === selectedProductId;
        fragment.appendChild(option);
      });

      select.replaceChildren(fragment);
      select.value = selectedProductId;
    }

    _renderVariantOptions(select, product, rowState) {
      var fragment = document.createDocumentFragment();
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = MESSAGES.chooseSizeOption;
      fragment.appendChild(placeholder);

      var selectedVariant = this._findVariant(product, rowState.variantId);
      if (selectedVariant && !selectedVariant.available) {
        selectedVariant = null;
        rowState.variantId = '';
        rowState.sizeLabel = '';
      }

      if (product) {
        product.variants.forEach(function (variant) {
          var option = document.createElement('option');
          option.value = variant._id;
          option.textContent = variant.sizeLabel + (variant.available ? '' : MESSAGES.soldOutSuffix);
          option.disabled = !variant.available;
          option.setAttribute('data-size-label', variant.sizeLabel);
          option.selected = Boolean(selectedVariant && variant._id === selectedVariant._id);
          fragment.appendChild(option);
        });
      }

      select.replaceChildren(fragment);
      select.value = selectedVariant ? selectedVariant._id : '';
    }

    _renderImage(rowElement, product) {
      var wrap = rowElement.querySelector(SELECTORS.imageWrap);
      var image = rowElement.querySelector(SELECTORS.image);
      var placeholder = rowElement.querySelector(SELECTORS.imagePlaceholder);

      if (!wrap) {
        return;
      }

      if (product && product.featuredImage) {
        if (!image) {
          image = document.createElement('img');
          image.className = 'lifta-mix-match__image';
          image.setAttribute('data-mix-match-image', '');
          image.width = 80;
          image.height = 120;
          image.loading = 'lazy';
          wrap.appendChild(image);
        }

        image.src = product.featuredImage;
        image.alt = product.featuredImageAlt || product.title;
        image.hidden = false;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        if (placeholder) placeholder.hidden = true;
      } else {
        if (image) {
          image.hidden = true;
          image.removeAttribute('src');
          image.alt = '';
        }
        if (placeholder) placeholder.hidden = false;
      }
    }

    _renderPrices() {
      this.config.tiers.forEach(function (tier) {
        var normalTotal = 0;
        var estimated = false;

        for (var index = 0; index < tier.quantity; index += 1) {
          var rowPrice = this._rowPrice(index);
          normalTotal += rowPrice.price;
          estimated = estimated || rowPrice.estimated;
        }

        var discountedTotal = Math.max(0, normalTotal - tier.discountAmount);
        var savings = normalTotal - discountedTotal;

        this._writeTierMoney(SELECTORS.tierPrice, 'data-mix-match-tier-price', tier.key, discountedTotal);
        this._writeTierMoney(SELECTORS.tierNormal, 'data-mix-match-tier-normal', tier.key, normalTotal);
        this._writeTierMoney(SELECTORS.tierDiscounted, 'data-mix-match-tier-discounted', tier.key, discountedTotal);
        this._writeTierMoney(
          SELECTORS.tierSavings,
          'data-mix-match-tier-savings',
          tier.key,
          savings,
          MESSAGES.savingsPrefix
        );

        this.querySelectorAll(SELECTORS.tierEstimate).forEach(function (element) {
          if (element.getAttribute('data-mix-match-tier-estimate') === tier.key) {
            element.hidden = !estimated;
          }
        });
        this.setAttribute('data-tier-' + tier.key + '-estimated', estimated ? 'true' : 'false');
      }, this);
    }

    _rowPrice(index) {
      var rowState = this.state.rows[index];
      var product = rowState ? this._findProduct(rowState.productId) : null;
      var variant = rowState ? this._findVariant(product, rowState.variantId) : null;

      if (variant && variant.available) {
        return { price: variant.price, estimated: false };
      }

      return { price: product ? product.priceMin : 0, estimated: true };
    }

    _writeTierMoney(selector, attribute, tierKey, cents, prefix) {
      var formatted = this._formatMoney(cents);
      this.querySelectorAll(selector).forEach(function (element) {
        if (element.getAttribute(attribute) === tierKey) {
          element.innerHTML = (prefix || '') + formatted;
        }
      });
    }

    _formatMoney(cents) {
      var themeObject = window.theme;

      if (themeObject && themeObject.Currency && typeof themeObject.Currency.formatMoney === 'function') {
        try {
          return themeObject.Currency.formatMoney(
            cents,
            themeObject.settings && themeObject.settings.moneyFormat
          );
        } catch (error) {
          // Fall through to the locale-aware browser formatter.
        }
      }

      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.config.currency
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2) + ' ' + this.config.currency;
      }
    }

    _findProduct(productId) {
      var normalizedId = idString(productId);
      return this.config.products.find(function (product) { return product._id === normalizedId; }) || null;
    }

    _findTier(tierKey) {
      var normalizedKey = idString(tierKey);
      return this.config.tiers.find(function (tier) { return tier.key === normalizedKey; }) || null;
    }

    _findVariant(product, variantId) {
      if (!product) {
        return null;
      }

      var normalizedId = idString(variantId);
      return product.variants.find(function (variant) { return variant._id === normalizedId; }) || null;
    }

    _validationResult() {
      if (this._markupInvalid || this.rows.length < this.state.activeQuantity) {
        return { valid: false, message: MESSAGES.invalidMarkup, field: null };
      }

      for (var index = 0; index < this.state.activeQuantity; index += 1) {
        var rowElement = this.rows[index];
        var rowState = this.state.rows[index];
        var productSelect = rowElement && rowElement.querySelector(SELECTORS.product);
        var variantSelect = rowElement && rowElement.querySelector(SELECTORS.variant);
        var product = rowState ? this._findProduct(rowState.productId) : null;

        if (!product) {
          return {
            valid: false,
            message: MESSAGES.chooseColor.replace('[row]', String(index + 1)),
            field: productSelect
          };
        }

        var variant = this._findVariant(product, rowState.variantId);
        if (!variant) {
          return {
            valid: false,
            message: MESSAGES.chooseSize.replace('[row]', String(index + 1)),
            field: variantSelect
          };
        }

        if (!variant.available) {
          return {
            valid: false,
            message: MESSAGES.unavailable.replace('[row]', String(index + 1)),
            field: variantSelect
          };
        }
      }

      return { valid: true, message: '', field: null };
    }

    _validate(shouldFocus) {
      this._clearInvalidFields();
      var result = this._validationResult();

      if (result.valid) {
        this._clearError();
        return result;
      }

      if (result.field) {
        result.field.setAttribute('aria-invalid', 'true');
      }
      this._setError(result.message);

      if (shouldFocus && result.field && !result.field.disabled) {
        result.field.focus();
      }

      return result;
    }

    _clearInvalidFields() {
      this.querySelectorAll(SELECTORS.product + ', ' + SELECTORS.variant).forEach(function (select) {
        select.removeAttribute('aria-invalid');
      });
    }

    _setError(message) {
      if (!this.errorElement) {
        return;
      }

      this.errorElement.textContent = message || '';
      this.errorElement.hidden = !message;
    }

    _clearError() {
      this._setError('');
    }

    _updateButton() {
      if (!this.addButton) {
        return;
      }

      this.addButton.disabled = this.state.loading || !this._validationResult().valid;
      if (this.state.loading) {
        this.addButton.setAttribute('aria-busy', 'true');
        this.addButton.classList.add('btn--loading');
      } else {
        this.addButton.removeAttribute('aria-busy');
        this.addButton.classList.remove('btn--loading');
      }
    }

    _setLoading(loading) {
      this.state.loading = loading;
      this.classList.toggle('is-loading', loading);

      if (loading) {
        this._resetButtonText();
      }

      this._render();
    }

    _resetButtonText() {
      if (this._successTimer) {
        window.clearTimeout(this._successTimer);
        this._successTimer = null;
      }

      if (this.buttonTextElement) {
        this.buttonTextElement.textContent = this.defaultButtonText;
      }
    }

    _showSuccessText() {
      this._resetButtonText();
      if (!this.buttonTextElement) {
        return;
      }

      this.buttonTextElement.textContent = MESSAGES.added;
      this._successTimer = window.setTimeout(function () {
        this._successTimer = null;
        if (this.buttonTextElement) {
          this.buttonTextElement.textContent = this.defaultButtonText;
        }
      }.bind(this), 1800);
    }

    _aggregateItems() {
      var quantities = new Map();
      var originalIds = new Map();

      for (var index = 0; index < this.state.activeQuantity; index += 1) {
        var rowState = this.state.rows[index];
        var product = this._findProduct(rowState.productId);
        var variant = this._findVariant(product, rowState.variantId);
        var key = variant._id;

        quantities.set(key, (quantities.get(key) || 0) + 1);
        if (!originalIds.has(key)) {
          originalIds.set(key, variant.id);
        }
      }

      return Array.from(quantities.entries()).map(function (entry) {
        return { id: originalIds.get(entry[0]), quantity: entry[1] };
      });
    }

    async _addSelectedItems() {
      if (this.state.loading) {
        return;
      }

      this.state.validationAttempted = true;
      var validation = this._validate(true);
      this._updateButton();

      if (!validation.valid) {
        return;
      }

      var items = this._aggregateItems();
      var themeObject = window.theme;
      var endpoint = themeObject && themeObject.routes && themeObject.routes.cartAdd
        ? themeObject.routes.cartAdd
        : localeRoute('cart/add.js');
      var payload;

      this._setLoading(true);

      try {
        var response = await window.fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        });

        payload = await this._parseResponse(response);
        var payloadStatus = payload && Number(payload.status);
        var payloadHasErrorStatus = Number.isFinite(payloadStatus) && payloadStatus >= 400;

        if (!response.ok || payloadHasErrorStatus || payload === null) {
          var responseError = new Error('Cart add failed');
          responseError.payload = payload;
          throw responseError;
        }

        this._setLoading(false);
        this.state.validationAttempted = false;
        this._clearInvalidFields();
        this._clearError();
        this._showSuccessText();

        this.dispatchEvent(new CustomEvent('lifta:mix-match:added', {
          bubbles: true,
          detail: { items: items, response: payload }
        }));

        this._refreshCartAfterSuccess(payload);
      } catch (error) {
        payload = error && typeof error.payload !== 'undefined' ? error.payload : payload;
        this._setLoading(false);
        this._setError(this._cartErrorMessage(payload));
        this._refreshCartAfterError();
      }
    }

    async _parseResponse(response) {
      var text = await response.text();
      if (!text) {
        return null;
      }

      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    }

    _cartErrorMessage(payload) {
      if (payload && typeof payload.description === 'string' && payload.description.trim()) {
        return payload.description;
      }
      if (payload && typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message;
      }
      return MESSAGES.addFailed;
    }

    _updateAmpSlideCart() {
      if (typeof window.SLIDECART_UPDATE !== 'function') {
        return false;
      }

      try {
        window.SLIDECART_UPDATE(function () {
          if (typeof window.SLIDECART_OPEN === 'function') {
            window.SLIDECART_OPEN();
          }
        });
        return true;
      } catch (error) {
        return false;
      }
    }

    _refreshAmpSlideCartAfterError() {
      if (typeof window.SLIDECART_UPDATE !== 'function') {
        return false;
      }

      try {
        window.SLIDECART_UPDATE(function () {});
        return true;
      } catch (error) {
        return false;
      }
    }

    _dispatchStreamlineAdded(response) {
      document.body.classList.add('cart-has-items');
      this.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        bubbles: true,
        detail: {
          product: response,
          addToCartBtn: this.addButton
        }
      }));
    }

    _refreshCartAfterSuccess(response) {
      if (this._updateAmpSlideCart()) {
        return;
      }

      var themeObject = window.theme;
      var cartType = themeObject && themeObject.settings && themeObject.settings.cartType;

      if (cartType === 'drawer' || cartType === 'sticky') {
        this._dispatchStreamlineAdded(response);
        return;
      }

      var cartPage = themeObject && themeObject.routes && themeObject.routes.cartPage
        ? themeObject.routes.cartPage
        : localeRoute('cart');
      window.location.assign(cartPage);
    }

    _refreshCartAfterError() {
      if (this._refreshAmpSlideCartAfterError()) {
        return;
      }

      var themeObject = window.theme;
      var cartType = themeObject && themeObject.settings && themeObject.settings.cartType;

      if (cartType === 'drawer') {
        document.dispatchEvent(new CustomEvent('cart:build'));
        this._refreshCartSnapshot();
        return;
      }

      if (cartType === 'sticky') {
        if (themeObject && typeof themeObject.refreshCart === 'function') {
          themeObject.refreshCart();
        }
        this._refreshCartSnapshot();
        return;
      }

      this._refreshCartSnapshot();
    }

    async _refreshCartSnapshot() {
      var themeObject = window.theme;
      var endpoint = themeObject && themeObject.routes && themeObject.routes.cart
        ? themeObject.routes.cart
        : localeRoute('cart.js');

      try {
        var response = await window.fetch(endpoint, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        if (!response.ok) return;

        var cart = await response.json();
        if (!cart || !Number.isFinite(Number(cart.item_count))) return;

        var count = Number(cart.item_count);
        document.body.classList.toggle('cart-has-items', count > 0);
        document.querySelectorAll('.cart-link__bubble-num').forEach(function (element) {
          element.textContent = String(count);
        });
        document.querySelectorAll('.cart-link__bubble').forEach(function (element) {
          element.classList.toggle('cart-link__bubble--visible', count > 0);
        });
      } catch (error) {
        // The original cart error stays visible; a refresh failure must not hide it.
      }
    }

    _showFatalError() {
      this.errorElement = this.errorElement || this.querySelector(SELECTORS.error);
      this.addButton = this.addButton || this.querySelector(SELECTORS.add);
      if (this.addButton) this.addButton.disabled = true;
      this._setError(MESSAGES.invalidData);
    }
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, LiftaMixMatchBundle);
  }
}());
